// A right-hand panel over a scrim, for a task that sits beside the page it came
// from (the drone page's repair pulls). Esc and a click on the scrim close it,
// Tab stays inside, and focus goes back to the key that opened it.
// Focus on open is the child's call: a child that already took focus (the scan
// field) keeps it; otherwise the close button gets it.

import React, { useEffect, useRef, useState } from 'react';
import { IconButton } from '../../components/ui/Button';
import Tooltip from '../../components/ui/Tooltip';
import { IconClose } from '../../components/ui/icons';
import { createPortal } from 'react-dom';

let seq = 0;

const TABBABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  title: string;
  onClose: () => void;
  /** True while the owner is holding the drawer mounted through its exit. */
  leaving?: boolean;
  children: React.ReactNode;
}

const SideDrawer: React.FC<Props> = ({ title, onClose, leaving = false, children }) => {
  const titleId = useRef(`side-drawer-${++seq}`);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Read during the first render, before any child's mount effect moves focus inside.
  const [opener] = useState(() => (typeof document === 'undefined' ? null : document.activeElement as HTMLElement | null));
  // Two-step mount so the slide transitions instead of appearing.
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!panelRef.current?.contains(document.activeElement)) closeRef.current?.focus();
    }, 0);
    return () => {
      clearTimeout(t);
      if (opener && document.body.contains(opener)) opener.focus();
    };
  }, [opener]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(TABBABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId.current}
      onKeyDown={onKeyDown}
      className={`scrim z-[100] flex justify-end transition-opacity duration-exit ease-exit motion-reduce:transition-none ${
        leaving ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`flex h-full w-full max-w-md flex-col overflow-y-auto bg-surface-container-low shadow-2xl transition-transform duration-200 ease-emphasized motion-reduce:transition-none ${
          shown && !leaving ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 pb-2 pt-4">
          <h2 id={titleId.current} className="text-base font-semibold text-on-surface">
            {title}
          </h2>
          <Tooltip label="Close the drawer">
            <IconButton ref={closeRef} label="Close" onClick={onClose} className="-mr-1">
              <IconClose />
            </IconButton>
          </Tooltip>
        </div>
        <div className="flex-1 space-y-6 px-5 pb-6 pt-2">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default SideDrawer;
